/**
 * Created by Ning Jiang on 2022-05-16.
 */

import {WinPresentationController} from "../WinPresentationController";
import {Serial} from "../../Serial";
import {gsap} from "gsap";
import {RevealedSymbol} from "../../symbol/RevealedSymbol";
import {IBaseSymbol} from "../../../core/symbol/BaseSymbol";
import {Layers, SerialLayers} from "../../view/SerialLayers";
import {SerialSound} from "../../sound/SerialSound";
import {XSplitSymbol} from "../../symbol/XSplitSymbol";

export class XSplitController extends WinPresentationController {

    private _delays:number[] = [1.23333, 1.1, 1.06667, 0.76667];
    private _doubleDelays:number[] = [1.56667, 1.23333, 1.06667, 0.76667];

    constructor() {
        super("XSplitController", false);

        // to restore #234
        if(Serial.model.isRestore && !Serial.model.initData.nextMode.includes("RESPIN")) {
            Serial.model.initData.symbolSizes.forEach((reelSize, reelId) => {
                if(Serial.model.initData.multiplierOnReel[reelId] > 0) {
                    return;
                }
                reelSize.forEach((symSize, symId) => {
                    if(symSize == 0) {
                        return;
                    }
                    if(Serial.model.initData.evaluatedArea[reelId][symId].includes("S")) {
                        return;
                    }
                    const symbol = Serial.getSymbol(reelId, symId);
                    const reveal = new RevealedSymbol(Serial.model.initData.evaluatedArea[reelId][symId], reelId, symbol.symbolHeight, symSize);
                    (symId > 1 && symId < 5) ? Serial.reels[reelId].replaceSymbol(reveal, symId - 2) : Serial.enhancerReels[reelId][symId].replaceSymbol(reveal);
                    symbol.onRemove();
                });
            });
        }
    }

    protected hasWin():boolean {
        const data = Serial.model.parsedGameData;
        return !data.nextMode.includes("RESPIN") && data.scatterPositions[4] > -1;
    }

    protected async createWinAnimation():Promise<any> {
        const position = Serial.model.parsedGameData.scatterPositions[4];
        const tl = gsap.timeline();
        tl.add(this.getSplitAnimation());
        for(let reelId = 3; reelId >= 0; reelId--) {
            const delay = (Serial.model.parsedGameData.mode.includes("FREESPIN") && Serial.model.parsedGameData.scatterPositions[4] != 3) ? this._doubleDelays[reelId] : this._delays[reelId];
            if(Serial.wildNudgeController.enhancerOverlays[reelId]) {
                // do not change ways when xSplit xNudge.
                tl.add(Serial.wildNudgeController.enhancerOverlays[reelId]!.splitSymbol(), delay);
            } else {
                const symbol = Serial.getSymbol(reelId, position);
                tl.add(this.splitSymbol(symbol, reelId, position), delay);
                const enhancerPosition = this.getHiddenEnhancerPosition(reelId, position);
                if(enhancerPosition > -1) {
                    const enhancerSymbol = Serial.getSymbol(reelId, enhancerPosition);
                    tl.add(this.splitSymbol(enhancerSymbol, reelId, enhancerPosition), delay);
                }
            }
        }

        return Promise.resolve(tl);
    }

    private getHiddenEnhancerPosition(reelId:number, scatterPosition:number):number {
        if(!Serial.enhancerReels[reelId][1].isOpened) {
            return -1;
        }
        if(scatterPosition === 2) {
            return 1;
        }
        if(scatterPosition === 4) {
            return 5
        }

        return -1;
    }

    private getSplitAnimation():TimelineLite {
        let splitAnimation = new PIXI.spine.Spine(Serial.assetLoader.getSpineAsset("xSplit"));
        splitAnimation.autoUpdate = true;
        const sym = <XSplitSymbol>Serial.getSymbol(4, Serial.model.parsedGameData.scatterPositions[4]);
        const position = sym.getGlobalPosition();
        splitAnimation.position.set(position.x, position.y);
        const animationName = this.getSplitAnimationName();
        const tl = gsap.timeline();
        tl.add(() => {SerialLayers.getLayer(Layers.WIN_PRESENTATIONS)!.addChild(splitAnimation)});
        tl.add(() => {
            SerialSound.soundPlayer.playTrack({name : SerialSound.X_SPLIT});
            splitAnimation.state.setAnimation(0, animationName, false);
            Serial.background.playCutEffect();
        });
        tl.to(this, {duration : splitAnimation.spineData.findAnimation(animationName).duration});
        tl.add(() => {
            sym.afterCut();
            splitAnimation.parent.removeChild(splitAnimation);
            splitAnimation.destroy();
        });
        return tl;
    }

    private getSplitAnimationName():string {
        if(Serial.model.parsedGameData.mode.includes("FREESPIN")) {
            if(Serial.model.parsedGameData.scatterPositions[4] === 2) {
                return "Up";
            }
            if(Serial.model.parsedGameData.scatterPositions[4] === 4) {
                return "Down";
            }
        }

        return "Mid";
    }

    private splitSymbol(symbol:IBaseSymbol, reelId:number, symbolId:number):gsap.core.Animation {
        if(symbol.splitSymbol) {
            return symbol.splitSymbol(symbolId);
        }

        const symbolSize = Serial.model.parsedGameData.symbolSizes[reelId][symbolId];
        const reveal = new RevealedSymbol(symbol.symbolName, reelId, symbol.symbolHeight, symbolSize);
        const tl = gsap.timeline();
        tl.add(() => {
            (symbolId > 1 && symbolId < 5) ? Serial.reels[reelId].replaceSymbol(reveal, symbolId - 2) : Serial.enhancerReels[reelId][symbolId].replaceSymbol(reveal);
            symbol.onRemove();
            Serial.ways.updateWays(reelId, symbolId, symbolSize);
        });
        tl.add(gsap.to(reveal.scale, {x : 1.1, y : 1.1, duration : 0.1}));
        tl.add(gsap.to(reveal.scale, {x : 1, y : 1, duration : 0.3}));

        return tl;
    }
}